import React, { useState, useReducer, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import {
    GridComponent,
    ColumnsDirective,
    ColumnDirective,
    Inject,
    Page,
} from "@syncfusion/ej2-react-grids";

// Components
import Spinner from "../Components/Spinner";
import InputField from "../Components/Forms/InputField";
import { ToastComponent } from "../Components/ToastComponent";

// Services
import { supplierService } from "../../Services/suppliers.service";

// Icons
import { AiFillSave } from "react-icons/ai";
import { FcSimCardChip } from "react-icons/fc";
import { FaEdit, FaTrash } from "react-icons/fa";

const _pageTitle = "Cadastro de Fornecedores";
const _registerTitle = "Cadastrando novo Fornecedor";
const _editTitle = "Editando Fornecedor";

const formInitialValues = {
    id: 0,
    supplierName: "",
    commercialContactName: "",
    commercialPhone: "",
    commercialEmail: "",
    supportPhone: "",
    supportEmail: "",
    supportWhatsApp: "",
};

const formReducer = (state, event) => {
    if (event.setter) {
        return event.supplier;
    }
    if (event.reset) {
        return formInitialValues;
    }

    return {
        ...state,
        [event.name]: event.value,
    };
};

const alertReducer = (state, event) => {
    if (event.reset) {
        return { show: false, message: "", color: "danger" };
    }
    return {
        ...state,
        show: event.show,
        message: event.message,
        color: event.color,
    };
};

const mapSupplierToForm = (supplier) => ({
    id: supplier?.SupplierId ?? supplier?.supplierId ?? supplier?.id ?? 0,
    supplierName:
        supplier?.SupplierCode ??
        supplier?.supplierCode ??
        supplier?.SupplierName ??
        supplier?.supplierName ??
        "",
    commercialContactName:
        supplier?.CommercialContactName ?? supplier?.commercialContactName ?? "",
    commercialPhone: supplier?.CommercialPhone ?? supplier?.commercialPhone ?? "",
    commercialEmail: supplier?.CommercialEmail ?? supplier?.commercialEmail ?? "",
    supportPhone: supplier?.SupportPhone ?? supplier?.supportPhone ?? "",
    supportEmail: supplier?.SupportEmail ?? supplier?.supportEmail ?? "",
    supportWhatsApp: supplier?.SupportWhatsApp ?? supplier?.supportWhatsApp ?? "",
});

const formatPhoneForDisplay = (value) => {
    const numbers = (value || "").replace(/\D/g, "");

    if (!numbers) {
        return "";
    }

    if (numbers.length <= 2) {
        return numbers;
    }

    if (numbers.length <= 7) {
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    }

    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
};

const mapSupplierToGrid = (supplier) => ({
    id: supplier?.SupplierId ?? supplier?.supplierId ?? supplier?.id ?? 0,
    supplierCode:
        supplier?.SupplierCode ??
        supplier?.supplierCode ??
        supplier?.SupplierName ??
        supplier?.supplierName ??
        "",
    active: supplier?.Active ?? supplier?.active ?? "",
    commercialContactName:
        supplier?.CommercialContactName ?? supplier?.commercialContactName ?? "",
    commercialPhone: formatPhoneForDisplay(
        supplier?.CommercialPhone ?? supplier?.commercialPhone ?? ""
    ),
    commercialEmail: supplier?.CommercialEmail ?? supplier?.commercialEmail ?? "",
    supportPhone: formatPhoneForDisplay(
        supplier?.SupportPhone ?? supplier?.supportPhone ?? ""
    ),
    supportEmail: supplier?.SupportEmail ?? supplier?.supportEmail ?? "",
    supportWhatsApp: formatPhoneForDisplay(
        supplier?.SupportWhatsApp ?? supplier?.supportWhatsApp ?? ""
    ),
});

export default function Suppliers() {
    const [formData, setFormData] = useReducer(formReducer, formInitialValues);
    const [isLoading, setIsLoading] = useState(false);
    const [toastParams, setToastParams] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [actionTitle, setActionTitle] = useState("Dado do fornecedor");
    const [showFields, setShowFields] = useState(false);
    const [alert, setAlert] = useReducer(alertReducer, {
        show: false,
        message: "",
        color: "danger",
    });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [supplierToDelete, setSupplierToDelete] = useState(null);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch({ type: "title", value: _pageTitle });
    }, [dispatch]);

    useEffect(() => {
        onSearch();
    }, []);

    const onSearch = async () => {
        setAlert({ reset: true });
        setShowFields(false);

        try {
            setIsLoading(true);
            const suppliers = await supplierService.list({
                Active: "S",
            });

            if (suppliers && suppliers.length > 0) {
                setSearchResults(suppliers.map((item) => mapSupplierToGrid(item)));
            } else {
                setSearchResults([]);
                setAlert({
                    show: true,
                    message: "Nenhum fornecedor encontrado para o filtro informado.",
                    color: "warning",
                });
            }
        } catch (error) {
            setAlert({
                show: true,
                message: "Não foi possível realizar a busca no momento.",
                color: "danger",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const onEditSupplier = (row) => {
        setFormData({
            setter: true,
            supplier: mapSupplierToForm(row),
        });
        setShowFields(true);
        setActionTitle(_editTitle);
    };

    const onDeleteSupplier = (row) => {
        const supplierId = row?.id;
        if (!supplierId) {
            return;
        }

        setSupplierToDelete(row);
        setShowDeleteModal(true);
    };

    const confirmDeleteSupplier = async () => {
        const supplierId = supplierToDelete?.id;
        if (!supplierId) {
            return;
        }

        try {
            setIsLoading(true);
            await supplierService.remove(supplierId);
            setSearchResults((prev) => prev.filter((item) => item.id !== supplierId));
            setShowDeleteModal(false);
            setSupplierToDelete(null);
            setAlert({
                show: true,
                message: "Fornecedor excluído com sucesso!",
                color: "success",
            });

            if (formData?.id === supplierId) {
                setFormData({ reset: true });
                setShowFields(false);
            }
        } catch (error) {
            setAlert({
                show: true,
                message: "Não foi possível excluir o fornecedor.",
                color: "danger",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const onNew = () => {
        setAlert({ reset: true });
        setFormData({ reset: true });
        setShowFields(true);
        setActionTitle(_registerTitle);
    };

    const onCancel = () => {
        setAlert({ reset: true });
        setFormData({ reset: true });
        setShowFields(false);
    };

    const onSave = async () => {
        if (!formData.supplierName) {
            setAlert({
                show: true,
                message: "Forneça o Nome do Fornecedor!",
                color: "danger",
            });
            return;
        }

        try {
            setIsLoading(true);
            const unformatNumber = (value) => (value || "").replace(/\D/g, "");
            const payload = {
                id: formData.id,
                supplierName: formData.supplierName,
                commercialContactName: formData.commercialContactName,
                commercialPhone: unformatNumber(formData.commercialPhone),
                commercialEmail: formData.commercialEmail,
                supportPhone: unformatNumber(formData.supportPhone),
                supportEmail: formData.supportEmail,
                supportWhatsApp: unformatNumber(formData.supportWhatsApp),
            };

            const result = await supplierService.save(payload);

            if (result) {
                if (result?.id) {
                    setFormData({
                        name: "id",
                        value: result.id,
                    });
                }

                const suppliersAfterSave = await supplierService.list({ Active: "S" });

                if (suppliersAfterSave && suppliersAfterSave.length > 0) {
                    setSearchResults(suppliersAfterSave.map((item) => mapSupplierToGrid(item)));
                } else if (result?.id) {
                    setSearchResults((prev) => {
                        const payloadToGrid = {
                            id: result.id,
                            supplierCode: formData.supplierName,
                            active: "S",
                            commercialContactName: formData.commercialContactName,
                            commercialPhone: formData.commercialPhone,
                            commercialEmail: formData.commercialEmail,
                            supportPhone: formData.supportPhone,
                            supportEmail: formData.supportEmail,
                            supportWhatsApp: formData.supportWhatsApp,
                        };

                        const index = prev.findIndex((item) => item.id === result.id);
                        if (index >= 0) {
                            const updated = [...prev];
                            updated[index] = payloadToGrid;
                            return updated;
                        }

                        return [payloadToGrid, ...prev];
                    });
                }

                if (result.isCreated) {
                    setToastParams(JSON.stringify({
                        show: true,
                        title: "Sucesso",
                        body: "Fornecedor criado com sucesso!",
                        color: "success"
                    }));
                } else {
                    setAlert({
                        show: true,
                        message: "Fornecedor salvo com sucesso!",
                        color: "success",
                    });
                }

                setShowFields(false);
            }
        } catch (error) {
            setAlert({
                show: true,
                message: "Não foi possível salvar. tente novamente mais tarde.",
                color: "danger",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const formatPhone = (value) => {
        // Remove caracteres não numéricos
        const numbers = value.replace(/\D/g, "");

        // Aplica máscara (XX) XXXXX-XXXX
        if (numbers.length <= 2) {
            return numbers;
        } else if (numbers.length <= 7) {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
        } else {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
        }
    };

    const handlePhoneChange = (fieldName) => (event) => {
        const formatted = formatPhone(event.target.value);
        setFormData({
            name: fieldName,
            value: formatted,
        });
    };

    const handleFormChange = async (event) => {
        setFormData({
            name: event.target.name,
            value: event.target.value,
        });
    };

    return (
        <>
            <div className="container-xxl flex-grow-1 container-p-y">
                {isLoading ? <Spinner /> : ""}

                <ToastComponent
                    params={toastParams}
                    closeToast={() => setToastParams(JSON.stringify({ show: false }))}
                />

                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirmar exclusão</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Deseja realmente excluir o fornecedor
                        {" "}
                        <strong>{supplierToDelete?.supplierCode || supplierToDelete?.id}</strong>
                        ?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancelar
                        </Button>
                        <Button variant="danger" onClick={confirmDeleteSupplier}>
                            Excluir
                        </Button>
                    </Modal.Footer>
                </Modal>

                <div className="row">
                    <h5>Lista carregada automaticamente. Crie um novo quando necessário</h5>
                    <div className="row">
                        <div className="mb-3 col-12">
                            <div className="d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={showFields}
                                    onClick={onNew}
                                    tabIndex={4}
                                >
                                    <FcSimCardChip className="icon-in-button" />
                                    &nbsp; Novo
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {alert?.show ? (
                    <div
                        className={`alert alert-${alert.color} alert-dismissible`}
                        role="alert"
                    >
                        <div dangerouslySetInnerHTML={{ __html: alert?.message }}></div>

                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="alert"
                            onClick={() => setAlert({ show: false, message: "" })}
                            aria-label="Close"
                        ></button>
                    </div>
                ) : (
                    ""
                )}

                <div
                    className="row"
                    style={showFields ? { display: "" } : { display: "none" }}
                >
                    <h4 className="fw-bold py-3 mb-4">
                        <span className="text-muted fw-light">{actionTitle} </span>
                    </h4>

                    {formData.id ? (
                        <div className="row mb-1">
                            <label className="col-sm-4 col-form-label" htmlFor="supplierName">
                                Id: {formData.id || ""}
                            </label>
                        </div>
                    ) : (
                        ""
                    )}

                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <InputField
                                label="Nome do Fornecedor"
                                name="supplierName"
                                type="text"
                                tabIndex="5"
                                grid_length="10"
                                value={formData.supplierName || ""}
                                onChange={handleFormChange}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <InputField
                                label="Nome do Contato Comercial"
                                name="commercialContactName"
                                type="text"
                                tabIndex="6"
                                grid_length="10"
                                value={formData.commercialContactName || ""}
                                onChange={handleFormChange}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <InputField
                                label="Telefone Comercial"
                                name="commercialPhone"
                                type="text"
                                tabIndex="7"
                                grid_length="10"
                                value={formData.commercialPhone || ""}
                                onChange={handlePhoneChange("commercialPhone")}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <InputField
                                label="E-mail Comercial"
                                name="commercialEmail"
                                type="text"
                                tabIndex="8"
                                grid_length="10"
                                value={formData.commercialEmail || ""}
                                onChange={handleFormChange}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <InputField
                                label="Telefone Suporte"
                                name="supportPhone"
                                type="text"
                                tabIndex="9"
                                grid_length="10"
                                value={formData.supportPhone || ""}
                                onChange={handlePhoneChange("supportPhone")}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <InputField
                                label="E-mail Suporte"
                                name="supportEmail"
                                type="text"
                                tabIndex="10"
                                grid_length="10"
                                value={formData.supportEmail || ""}
                                onChange={handleFormChange}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <InputField
                                label="WhatsApp Suporte"
                                name="supportWhatsApp"
                                type="text"
                                tabIndex="11"
                                grid_length="10"
                                value={formData.supportWhatsApp || ""}
                                onChange={handlePhoneChange("supportWhatsApp")}
                            />
                        </div>
                    </div>

                    <div className="mt-4">
                        <button
                            type="button"
                            tabIndex={12}
                            className="btn btn-primary me-2"
                            onClick={async () => onSave()}
                        >
                            <AiFillSave className="icon-in-button" />
                            &nbsp; Salvar
                        </button>

                        <button
                            type="reset"
                            className="btn btn-outline-secondary  me-2"
                            onClick={onCancel}
                            tabIndex={13}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>

                {!showFields && searchResults.length > 0 ? (
                    <div className="row mt-4">
                        <div className="col-12">
                            <h5>Resultado da busca</h5>
                            <GridComponent
                                dataSource={searchResults}
                                allowPaging={true}
                                pageSettings={{ pageSize: 10 }}
                            >
                                <ColumnsDirective>
                                    <ColumnDirective field="id" headerText="ID" width="80" textAlign="Center" />
                                    <ColumnDirective field="supplierCode" headerText="Código do Supplier" width="180" />
                                    <ColumnDirective field="commercialPhone" headerText="Telefone Comercial" width="150" />
                                    <ColumnDirective field="commercialEmail" headerText="E-mail Comercial" width="180" />
                                    <ColumnDirective field="supportPhone" headerText="Telefone Suporte" width="150" />
                                    <ColumnDirective field="supportWhatsApp" headerText="WhatsApp" width="150" />
                                    <ColumnDirective
                                        headerText="Ações"
                                        width="140"
                                        textAlign="Center"
                                        template={(row) => (
                                            <div className="d-flex gap-2 justify-content-center">
                                                <button
                                                    type="button"
                                                    className="text-primary"
                                                    onClick={() => onEditSupplier(row)}
                                                >
                                                    <FaEdit size={18} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-primary"
                                                    data-delete-action="true"
                                                    aria-label="Excluir"
                                                    onClick={() => onDeleteSupplier(row)}
                                                >
                                                    <FaTrash size={18} />
                                                </button>
                                            </div>
                                        )}
                                    />
                                </ColumnsDirective>
                                <Inject services={[Page]} />
                            </GridComponent>
                        </div>
                    </div>
                ) : (
                    ""
                )}
            </div>
        </>
    );
}
